import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../index';
import api from '../../utils/api';

// Define types for BODACC data
interface BodaccPublication {
  id: string;
  publicationavis: string;
  parution: string;
  dateparution: string;
  numeroannonce: string;
  typeavis: string;
  typeavis_lib: string;
  familleavis: string;
  familleavis_lib: string;
  numerodepartement: string;
  departement_nom_officiel: string;
  region_code: string;
  region_nom_officiel: string;
  tribunal: string;
  commercant: string;
  ville: string;
  registre: string;
  cp: string;
  pdf_parution_subfolder: string;
  ispdf_unitaire: string;
  listepersonnes: string;
  listeetablissements: string;
  jugement: string;
  acte: string;
  modificationsgenerales: string;
  radiationaurcs: string;
  depot: string;
  listeprecedentexploitant: string;
  listeprecedentproprietaire: string;
  divers: string;
  parutionavisprecedent: string;
  url_complete: string;
}

interface BodaccSiren {
  bodacc_id: string;
  siren: string;
  publicationavis: string;
  dateparution: string;
  publication: BodaccPublication;
  adherents?: any[];
}

interface CompanyInfo {
  siren: string;
  denominationUniteLegale?: string;
  nomUniteLegale?: string;
  prenomUsuelUniteLegale?: string;
  sexeUniteLegale?: string;
}

interface BodaccState {
  companyAdherentsBodacc: BodaccSiren[];
  bodaccData: BodaccSiren[];
  bodaccTotal: number;
  bodaccPage: number;
  bodaccLimit: number;
  bodaccSort: {
    field: string;
    order: 'asc' | 'desc';
  };
  sirenBodacc: {
    company: CompanyInfo | null;
    bodaccEntries: BodaccSiren[];
  };
  loading: boolean;
  error: string | null;
}

const initialState: BodaccState = {
  companyAdherentsBodacc: [],
  bodaccData: [],
  bodaccTotal: 0,
  bodaccPage: 1,
  bodaccLimit: 10,
  bodaccSort: {
    field: 'publication.dateparution',
    order: 'desc',
  },
  sirenBodacc: {
    company: null,
    bodaccEntries: [],
  },
  loading: false,
  error: null,
};

// Async thunks
export const fetchCompanyAdherentsBodacc = createAsyncThunk(
  'bodacc/fetchCompanyAdherentsBodacc',
  async ({ companyId, fromDate, search, familleAvis }: { 
    companyId: string; 
    fromDate?: string; 
    search?: string;
    familleAvis?: string;
  }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { bodaccPage: page, bodaccLimit: limit, bodaccSort: sort } = state.bodacc;
      
      // Build query parameters
      const params = new URLSearchParams();
      if (fromDate) {
        params.append('fromDate', fromDate);
      }
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sort', sort.field);
      params.append('order', sort.order);
      if (search) {
        params.append('search', search);
      }
      if (familleAvis) {
        params.append('familleAvis', familleAvis);
      }
      
      const response = await api.get(
        `/bodacc/company/${companyId}/adherents?${params.toString()}`
      );
      
      return {
        data: response.data.data,
        total: response.data.total || 0,
        page: response.data.page || 1,
        limit: response.data.limit || 10
      };
    } catch (_error) {
      return rejectWithValue('Une erreur est survenue lors de la récupération des données BODACC');
    }
  }
);

// Since we don't have a general BODACC endpoint, we'll use a mock implementation for now
// In a real application, you would need to add this endpoint to your backend
export const fetchBodacc = createAsyncThunk(
  'bodacc/fetchBodacc',
  async (
    { search, limit }: { search?: string; limit?: number },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const { bodaccPage: page, bodaccLimit, bodaccSort } = state.bodacc;
      
      // Use the actual limit or the one from state
      const actualLimit = limit || bodaccLimit;
      
      // Build query parameters
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', actualLimit.toString());
      params.append('sort', bodaccSort.field);
      params.append('order', bodaccSort.order);
      
      // Call the real backend endpoint
      const response = await api.get(`/bodacc/search?${params.toString()}`);
      
      return {
        data: response.data.data,
        total: response.data.total || 0,
      };
    } catch (_error) {
      return rejectWithValue('Une erreur est survenue lors de la récupération des données BODACC');
    }
  }
);

export const fetchSirenBodacc = createAsyncThunk(
  'bodacc/fetchSirenBodacc',
  async (sirenWithParams: string, { rejectWithValue }) => {
    try {
      // Split the siren and query params if they exist
      const [siren, queryParams] = sirenWithParams.split('?');
      
      // Build the URL
      let url = `/bodacc/siren/${siren}`;
      if (queryParams) {
        url += `?${queryParams}`;
      }
      
      console.log('Fetching BODACC data from:', url);
      const response = await api.get(url);
      console.log('BODACC API response:', response.data);
      
      // The API returns { success: true, data: [...], total: number, ... }
      // We need to transform this to match our state structure
      return {
        company: null, // We don't have company info in this response
        bodaccEntries: response.data.data || []
      };
    } catch (error) {
      console.error('Error fetching BODACC data:', error);
      return rejectWithValue('Une erreur est survenue lors de la récupération des données BODACC');
    }
  }
);

// Create slice
const bodaccSlice = createSlice({
  name: 'bodacc',
  initialState,
  reducers: {
    resetBodaccData: (state) => {
      state.sirenBodacc = {
        company: null,
        bodaccEntries: [],
      };
    },
    setBodaccPage: (state, action: PayloadAction<number>) => {
      state.bodaccPage = action.payload;
    },
    setBodaccLimit: (state, action: PayloadAction<number>) => {
      state.bodaccLimit = action.payload;
    },
    setBodaccSort: (state, action: PayloadAction<{ field: string; order: 'asc' | 'desc' }>) => {
      state.bodaccSort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCompanyAdherentsBodacc
      .addCase(fetchCompanyAdherentsBodacc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyAdherentsBodacc.fulfilled, (state, action) => {
        state.loading = false;
        state.companyAdherentsBodacc = action.payload.data;
        state.bodaccTotal = action.payload.total;
        state.bodaccPage = action.payload.page;
        state.bodaccLimit = action.payload.limit;
      })
      .addCase(fetchCompanyAdherentsBodacc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchBodacc
      .addCase(fetchBodacc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBodacc.fulfilled, (state, action) => {
        state.loading = false;
        state.bodaccData = action.payload.data;
        state.bodaccTotal = action.payload.total;
      })
      .addCase(fetchBodacc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchSirenBodacc
      .addCase(fetchSirenBodacc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSirenBodacc.fulfilled, (state, action) => {
        state.loading = false;
        state.sirenBodacc = action.payload;
      })
      .addCase(fetchSirenBodacc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetBodaccData, setBodaccPage, setBodaccLimit, setBodaccSort } = bodaccSlice.actions;

export const selectCompanyAdherentsBodacc = (state: RootState) => state.bodacc.companyAdherentsBodacc;
export const selectBodacc = (state: RootState) => state.bodacc.bodaccData;
export const selectBodaccTotal = (state: RootState) => state.bodacc.bodaccTotal;
export const selectBodaccPage = (state: RootState) => state.bodacc.bodaccPage;
export const selectBodaccLimit = (state: RootState) => state.bodacc.bodaccLimit;
export const selectBodaccSort = (state: RootState) => state.bodacc.bodaccSort;
export const selectSirenBodacc = (state: RootState) => state.bodacc.sirenBodacc;
export const selectBodaccLoading = (state: RootState) => state.bodacc.loading;
export const selectBodaccError = (state: RootState) => state.bodacc.error;

export default bodaccSlice.reducer; 